import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { Card } from "../components/Card.js";
import { Tag, BtnLink } from "../components/index.js";
import { Icon } from "../components/Icon.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { useAsync } from "../lib/useAsync.js";
import { api, manuals } from "../lib/api.js";
import type { Manual } from "@tebiki/shared";

/**
 * One manual version as returned by GET /api/manuals/:id/versions (→ { versions }).
 * Mirrors the API repo's ManualVersion shape (apps/api/src/repo/manualVersions.ts).
 */
type VersionState = "draft" | "published" | "archived";
interface ManualVersion {
  id: string;
  manualId: string;
  orgId: string;
  versionNo: number;
  state: VersionState;
  summary: string | null;
  createdBy: string;
  createdAt: number;
}

const STATE_LABEL: Record<VersionState, string> = {
  published: "公開中",
  draft: "下書き",
  archived: "アーカイブ",
};
const STATE_COLOR: Record<VersionState, string> = {
  published: "var(--ok)",
  draft: "var(--hi)",
  archived: "var(--muted)",
};

/** Short JP date/time for version metadata. */
function fmtWhen(ts: number): string {
  if (!ts) return "";
  const d = new Date(ts);
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function Versions() {
  const [params, setParams] = useSearchParams();
  const focusId = params.get("id");

  // Manuals to pick from (real, org-scoped).
  const manualState = useAsync<Manual[]>(async () => {
    const { items } = await manuals.list();
    return items;
  }, []);

  const list = manualState.data ?? [];
  // The manual whose history we show: the ?id deep-link if present & valid, else the first.
  const selectedId = useMemo(() => {
    if (focusId && list.some((m) => m.manualId === focusId)) return focusId;
    return list[0]?.manualId ?? "";
  }, [focusId, list]);
  const selected = list.find((m) => m.manualId === selectedId) ?? null;

  function pick(id: string) {
    const next = new URLSearchParams(params);
    if (id) next.set("id", id);
    else next.delete("id");
    setParams(next, { replace: true });
  }

  return (
    <ConsoleShell breadcrumb={["TEBIKI", "ライブラリ", "バージョン履歴"]}>
      <PageHead title={<>バージョン<span className="hl">履歴</span></>}>
        公開・下書き・アーカイブの版を時系列で確認。版を選んで内容をたどれます。
      </PageHead>

      {manualState.loading ? (
        <LoadingState label="マニュアルを読み込み中…" />
      ) : manualState.error ? (
        <ErrorState
          message={manualState.error}
          offline={manualState.offline}
          onRetry={manualState.reload}
        />
      ) : !list.length ? (
        <EmptyState
          icon={<Icon name="versions" size={30} />}
          title="まだマニュアルがありません"
          action={
            <BtnLink to="/library" variant="primary">
              ライブラリへ
            </BtnLink>
          }
        >
          マニュアルを作成して公開すると、ここに各マニュアルの版履歴が時系列で並びます。
        </EmptyState>
      ) : (
        <>
          <Card crop pad className="reveal">
            <label className="field-label" htmlFor="ver-manual">
              マニュアルを選択
            </label>
            <select
              id="ver-manual"
              className="select"
              value={selectedId}
              onChange={(e) => pick(e.target.value)}
            >
              {list.map((m) => (
                <option key={m.manualId} value={m.manualId}>
                  {m.title}
                </option>
              ))}
            </select>
          </Card>

          <div
            className="vlayout"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
              gap: 24,
              alignItems: "start",
              marginTop: 24,
            }}
          >
            {selected ? <VersionTimeline manual={selected} /> : null}

            {/* AI差分 — no diff endpoint exists yet; honest "準備中" panel. */}
            <Card crop pad className="reveal d1">
              <div className="kicker" style={{ marginBottom: 4 }}>
                DIFF · AI差分
              </div>
              <EmptyState icon={<Icon name="ai-review" size={30} />} title="版間のAI差分は準備中です">
                2つの版を選ぶと、手順・注釈・代表フレームの変更点をAIが要約して並べる予定です。
                現在は上の版タイムラインで各版の状態を確認できます。
              </EmptyState>
            </Card>
          </div>
        </>
      )}
    </ConsoleShell>
  );
}

/** Real per-manual version timeline (GET /api/manuals/:id/versions). */
function VersionTimeline({ manual }: { manual: Manual }) {
  const { data, loading, error, offline, reload } = useAsync<ManualVersion[]>(
    async () => {
      const res = await api<{ versions: ManualVersion[] }>(
        `/api/manuals/${manual.manualId}/versions`,
      );
      return res.versions;
    },
    [manual.manualId],
  );

  const versions = data ?? [];

  return (
    <Card crop pad className="reveal">
      <div className="spread" style={{ marginBottom: 12 }}>
        <div className="kicker">VERSIONS · {manual.title}</div>
        <BtnLink to={`/preview/${manual.manualId}`} variant="ghost">
          内容を開く
        </BtnLink>
      </div>

      {loading ? (
        <LoadingState label="版を読み込み中…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : !versions.length ? (
        <EmptyState icon={<Icon name="versions" size={30} />} title="まだ版がありません">
          このマニュアルが公開されると、版がここに記録されます。
        </EmptyState>
      ) : (
        <div role="list">
          {versions.map((v, i) => {
            const last = i === versions.length - 1;
            return (
              <div
                key={v.id}
                role="listitem"
                className="vnode"
                style={{ display: "grid", gridTemplateColumns: "18px 1fr", gap: 14 }}
              >
                <div
                  className="dotline"
                  style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                  aria-hidden="true"
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      marginTop: 5,
                      background:
                        v.state === "published" ? "var(--hi)" : "var(--surface)",
                      border:
                        v.state === "published"
                          ? "2px solid transparent"
                          : "2px solid var(--line-2)",
                    }}
                  />
                  {!last ? (
                    <span style={{ flex: 1, width: 2, background: "var(--line)" }} />
                  ) : null}
                </div>
                <div className="vbody" style={{ padding: "2px 0 22px" }}>
                  <div className="row" style={{ gap: 10, alignItems: "baseline" }}>
                    <b className="mono">v{v.versionNo}</b>
                    <Tag dot color={STATE_COLOR[v.state]}>
                      {STATE_LABEL[v.state]}
                    </Tag>
                  </div>
                  {v.summary ? (
                    <p className="jp" style={{ margin: "6px 0 4px", lineHeight: 1.6 }}>
                      {v.summary}
                    </p>
                  ) : null}
                  <div className="t-sm mono" style={{ color: "var(--muted)" }}>
                    {fmtWhen(v.createdAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
